import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export default function EmailMarketingSection() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const features = [
    {
      title: 'Gmail Campaign Strategy',
      description: 'Develop targeted email campaigns that reach your audience\'s inbox effectively.',
      icon: '📧',
      likes: '3.2K',
      comments: '245',
    },
    {
      title: 'List Management',
      description: 'Build and maintain quality email lists with Gmail best practices.',
      icon: '📋',
      likes: '2.9K',
      comments: '198',
    },
    {
      title: 'Content Creation',
      description: 'Create engaging email content optimized for Gmail\'s interface.',
      icon: '✍️',
      likes: '3.4K',
      comments: '256',
    },
    {
      title: 'Analytics & Optimization',
      description: 'Track delivery rates, open rates, and engagement metrics in Gmail.',
      icon: '📊',
      likes: '2.8K',
      comments: '187',
    },
  ];

  const benefits = [
    'Direct inbox delivery',
    'Cost-effective marketing',
    'Personalized messaging',
    'Higher open rates',
    'Better engagement',
    'Measurable results',
  ];

  return (
    <>
    
<Box
  sx={{
    position: 'fixed',
    top: { xs: 50, sm: 70 },
    left: { xs: 12, sm: 24 },
    zIndex: 1200,
    bgcolor: 'white',
    borderRadius: '50%',
    boxShadow:5,
    width: { xs: 40, sm: 48 },
    height: { xs: 40, sm: 48 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <IconButton
    onClick={() => navigate(-1)}
    size="medium"
    sx={{
      color: '#1877f2',
      p: 0.5
    }}
  >
    <ArrowBackIosNewIcon fontSize="small" />
  </IconButton>
</Box>
      {/* ✅ SEO Meta Tags */}
      <Helmet>
        <title>Email Marketing Services | SLGT Digital Marketing Agency</title>
        <meta
          name="description"
          content="Boost engagement with SLGT's Gmail marketing services. Personalized email campaigns, high open rates, and better conversions."
        />
        <meta
          name="keywords"
          content="Gmail Marketing, Smm, Social Media Marketing, Email Campaigns, Email Marketing Services India, Bulk Email, Email List Management, Gmail Campaign Agency"
        />
        <link rel="canonical" href="https://www.slgt.in/services/email-marketing" />
      </Helmet>

      {/* ✅ Main Email Marketing Section */}
      <Box sx={{ pt: { xs: 8, sm: 10 }, pb: { xs: 6, sm: 8 }, minHeight: '100vh', bgcolor: '#f0f2f5' }}>
        {/* Hero Section */}
        <Box sx={{ bgcolor: '#ffffff', color: '#ea4335', py: { xs: 6, md: 8 }, mb: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h1" sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  mb: 2,
                  color: '#ea4335',
                }}>
                  Reach Your Audience's Inbox
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: '#d93025' }}>
                  Build relationships and drive engagement with our expert Gmail marketing services.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/requestquotes')}
                  sx={{
                    bgcolor: '#ea4335',
                    color: 'white',
                    '&:hover': { bgcolor: '#d93025' },
                    px: 4,
                    py: 1.5,
                    borderRadius: '6px',
                    textTransform: 'none',
                  }}
                >
                  Get Started
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/assets/services/em-1.jpg"
                  alt="Gmail Marketing Services"
                  sx={{
                    width: '100%',
                    maxWidth: '500px',
                    borderRadius: 2,
                    display: 'block',
                    mx: 'auto',
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Container maxWidth="lg" sx={{ mb: 6 }}>
          <Typography variant="h2" sx={{ textAlign: 'center', mb: 4, color: '#ea4335', fontWeight: 700 }}>
            Our Gmail Marketing Services
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: '#ea4335', mr: 2 }}>{feature.icon}</Avatar>
                      <Typography sx={{ fontWeight: 600, color: '#ea4335' }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography sx={{ mb: 2 }}>{feature.description}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FavoriteIcon sx={{ color: '#ea4335', fontSize: 18 }} />
                        <Typography variant="caption">{feature.likes}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CommentIcon sx={{ color: '#ea4335', fontSize: 18 }} />
                        <Typography variant="caption">{feature.comments}</Typography>
                      </Box>
                      <IconButton size="small" sx={{ color: '#ea4335' }}>
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Benefits Section */}
        <Box sx={{ bgcolor: '#ffffff', py: 6 }}>
          <Container maxWidth="lg">
            <Typography variant="h2" sx={{ textAlign: 'center', mb: 4, color: '#ea4335', fontWeight: 700 }}>
              Benefits of Gmail Marketing
            </Typography>
            <Grid container spacing={3}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: '100%', border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: '#ea4335', mr: 2 }}>✓</Avatar>
                      <Typography sx={{ color: '#ea4335', fontWeight: 500 }}>{benefit}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Call-to-Action */}
        <Container maxWidth="lg" sx={{ mt: 6, textAlign: 'center' }}>
          <Card sx={{ bgcolor: '#ea4335', color: 'white', p: 4 }}>
            <Typography variant="h4" gutterBottom>
              Ready to Start Your Gmail Campaign?
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Let’s create a targeted email marketing strategy to boost your engagement and sales.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/requestquotes')}
              sx={{
                bgcolor: 'white',
                color: '#ea4335',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#fce8e6' },
              }}
            >
              Request a Quote
            </Button>
          </Card>
        </Container>
      </Box>
    </>
  );
  
}

